import React, {useEffect, useState} from "react";
import { AssignableAppraiser, ReassignOrderRequest} from "../api/ApiTypes.ts";
import {useAppraiserAdminApi} from "../context/ApiContext.tsx";
import SamButton from "./SamButton.tsx";
import {Send, Loader2} from "lucide-react";
import { AssignableAppraiserItem } from "./AssignableAppraiserItem.tsx";
import CancelButton from "./CancelButton.tsx";

interface ReAssignOrderProps {
    orderId: number,
    onSuccess: () => void;
    onCancel: () => void;
}

export const ReAssignOrderForm: React.FC<ReAssignOrderProps> = ({
                                                                    orderId,
                                                                    onSuccess,
                                                                    onCancel
                                                                }) => {

    const defaultState: ReassignOrderRequest = {
        orderId: orderId,
        appraiserToAssignTo: undefined
    };
    const [error, setError] = useState<string>('');
    const [isSending, setIsSending] = useState<boolean>(false);
    const [assignableAppraisers, setAssignableAppraisers] = useState<AssignableAppraiser[]>([]);
    const [formData, setFormData] = useState<ReassignOrderRequest>(defaultState);
    const [selectedAppraiser, setSelectedAppraiser] = useState<AssignableAppraiser | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const appraiserAdminApi = useAppraiserAdminApi()

    useEffect(() => {
        const getAssignableAppraisers = async () => {
            try {
                setIsLoading(true);
                const data = await appraiserAdminApi.getAssignableAppraisers(orderId);
                setAssignableAppraisers(data);
            } catch (err) {
                console.error("Failed to fetch assignable appraisers:", err);
                setAssignableAppraisers([]);
            }
            finally {
                setIsLoading(false);
            }
        };
        getAssignableAppraisers();
    }, []);

    const validateForm = (): boolean => {
        if (!formData.appraiserToAssignTo) {
            setError('Please select an appraiser to assign the order to.');
            return false;
        }
        if (!formData.orderId) {
            setError('No order id to assign!.');
            return false;
        }
        setError('');
        return true;
    };

    const handleAppraiserSelect = (appraiser: AssignableAppraiser) => {
        setSelectedAppraiser(appraiser);
        setFormData({
            ...formData,
            appraiserToAssignTo: appraiser.id
        });
        setError(''); // Clear any previous error when selection is made
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            setIsSending(true);
            // Call your API to send the message
            await appraiserAdminApi.reAssignOrder(formData);
            // Call onSuccess to notify parent component
            onSuccess();
        } catch (error) {
            console.error("Error sending message:", error);
            setError('Failed to send message. Please try again.');
        } finally {
            setIsSending(false);
        }
    };

    const handleCancel = () => {
        if (!isSending) {
            setFormData(defaultState);
            onCancel();
        }
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="mb-4">
                <h3 className="text-lg font-medium mb-2">Select an appraiser to reassign the order</h3>
                {error && <p className="text-red-500 text-sm mb-2">{error}</p>}

                <div className="space-y-4 max-h-96 overflow-y-auto">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center py-10">
                            <Loader2 className="h-8 w-8 text-blue-500 animate-spin mb-2" />
                            <p className="text-slate-600">Loading available appraisers...</p>
                        </div>
                    ) : assignableAppraisers.length > 0 ? (
                        assignableAppraisers.map((appraiser) => (
                            <div
                                key={appraiser.id}
                                onClick={() => handleAppraiserSelect(appraiser)}
                                className={`cursor-pointer ${selectedAppraiser?.id === appraiser.id ? 'ring-2 ring-blue-500' : ''}`}
                            >
                                <AssignableAppraiserItem
                                    appraiser={appraiser}
                                />
                            </div>
                        ))
                    ) : (
                        <p className="text-slate-500 py-4 text-center">No assignable appraisers available.</p>
                    )}
                </div>
            </div>

            {selectedAppraiser && (
                <div className="bg-blue-50 p-3 rounded-md mb-4">
                    <p className="font-medium">Selected Appraiser: {selectedAppraiser.firstName} {selectedAppraiser.lastName}</p>
                    <p className="text-sm text-slate-600">{selectedAppraiser.emailAddress}</p>
                </div>
            )}

            <div className="flex justify-end space-x-2">
                <CancelButton
                    type="button"
                    onClick={handleCancel}
                    disabled={isSending}
                >
                    Cancel
                </CancelButton>
                <SamButton
                    type="submit"
                    disabled={isLoading || isSending || !selectedAppraiser}
                >
                    <span className="inline-flex items-center space-x-1">
                        {isSending ? (
                            <>
                                <span className="animate-spin h-4 w-4 border-2 border-t-transparent border-white rounded-full mr-1"></span>
                                <span>Sending...</span>
                            </>
                        ) : (
                            <>
                                <Send className="h-4 w-4 mr-1" />
                                <span>Re-Assign</span>
                            </>
                        )}
                    </span>
                </SamButton>
            </div>
        </form>
    )
}